import { createClient, SupabaseClient } from '@supabase/supabase-js';

let supabaseInstance: SupabaseClient | null = null;
let initPromise: Promise<SupabaseClient> | null = null;

async function fetchConfig(): Promise<{ url: string; anonKey: string }> {
  const response = await fetch('/api/config');
  if (!response.ok) {
    throw new Error('Failed to fetch Supabase config');
  }
  return response.json();
}

export async function getSupabase(): Promise<SupabaseClient> {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  if (!initPromise) {
    initPromise = fetchConfig().then(({ url, anonKey }) => {
      supabaseInstance = createClient(url, anonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      });
      return supabaseInstance;
    });
  }

  return initPromise;
}

// Types matching the user's exact Supabase schema
export type Database = {
  public: {
    Tables: {
      user_roles: {
        Row: {
          user_id: string;
          role: 'staff' | 'manager' | 'admin';
        };
        Insert: {
          user_id: string;
          role: 'staff' | 'manager' | 'admin';
        };
        Update: {
          role?: 'staff' | 'manager' | 'admin';
        };
      };
      activity_codes: {
        Row: {
          code: string;
          name: string;
          billable_default: boolean;
          is_active: boolean;
          category: string;
          source: string | null;
          source_id: string | null;
        };
        Insert: {
          code: string;
          name: string;
          billable_default: boolean;
          is_active?: boolean;
          category?: string;
          source?: string | null;
          source_id?: string | null;
        };
        Update: {
          name?: string;
          billable_default?: boolean;
          is_active?: boolean;
          category?: string;
        };
      };
      accounts: {
        Row: {
          id: string;
          smartsuite_record_id: string | null;
          account_name: string;
          is_active: boolean;
          last_synced_at: string | null;
        };
        Insert: {
          id?: string;
          smartsuite_record_id?: string | null;
          account_name: string;
          is_active?: boolean;
        };
        Update: {
          smartsuite_record_id?: string | null;
          account_name?: string;
          is_active?: boolean;
          last_synced_at?: string | null;
        };
      };
      pay_periods: {
        Row: {
          id: string;
          start_date: string;
          end_date: string;
          pay_date: string;
          status: 'open' | 'closed';
          closed_at: string | null;
          closed_by: string | null;
        };
        Insert: {
          id?: string;
          start_date: string;
          end_date: string;
          pay_date: string;
          status?: 'open' | 'closed';
        };
        Update: {
          status?: 'open' | 'closed';
          closed_at?: string | null;
          closed_by?: string | null;
        };
      };
      time_entries: {
        Row: {
          id: string;
          user_id: string;
          entry_date: string;
          minutes: number;
          account_id: string | null;
          activity_code: string;
          notes: string | null;
          billable: boolean;
          status: 'draft' | 'submitted' | 'approved' | 'rejected';
          pay_period_id: string | null;
          review_note: string | null;
        };
        Insert: {
          id?: string;
          user_id: string;
          entry_date: string;
          minutes: number;
          account_id?: string | null;
          activity_code: string;
          notes?: string | null;
          billable: boolean;
          status?: 'draft' | 'submitted' | 'approved' | 'rejected';
          pay_period_id?: string | null;
        };
        Update: {
          entry_date?: string;
          minutes?: number;
          account_id?: string | null;
          activity_code?: string;
          notes?: string | null;
          billable?: boolean;
          status?: 'draft' | 'submitted' | 'approved' | 'rejected';
          review_note?: string | null;
        };
      };
      pto_policies: {
        Row: {
          user_id: string;
          accrual_hours_per_pay_period: number;
          starting_balance_hours: number;
          effective_start_date: string;
          annual_cap_hours: number | null;
        };
        Insert: {
          user_id: string;
          accrual_hours_per_pay_period: number;
          starting_balance_hours: number;
          effective_start_date: string;
          annual_cap_hours?: number | null;
        };
        Update: {
          accrual_hours_per_pay_period?: number;
          starting_balance_hours?: number;
          annual_cap_hours?: number | null;
        };
      };
      pto_ledger: {
        Row: {
          user_id: string;
          pay_period_id: string | null;
          entry_date: string;
          type: string;
          hours: number;
          note: string | null;
          created_by: string | null;
        };
        Insert: {
          user_id: string;
          pay_period_id?: string | null;
          entry_date: string;
          type: string;
          hours: number;
          note?: string | null;
          created_by?: string | null;
        };
        Update: {
          hours?: number;
          note?: string | null;
        };
      };
    };
  };
};
